import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Layout, message, Modal } from 'antd';
import { get } from 'lodash';
import CourierApi from 'services/courier';
import PdButton from 'components/pd-button';
import { PageSpinner } from 'components/page-spinner';
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './style.scss';
const { Content } = Layout;
const { confirm } = Modal;

export function CourierList() {
  const [couriersActive, setCouriersActive] = useState([]);
  const [couriersInactive, setCouriersInactive] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [limit] = useState(10);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );
  async function getCourierListActive() {
    const { data } = await CourierApi.getCourierList('active');
    const CourierList = data.data;
    setCouriersActive(CourierList);
  }

  async function getCourierListInactive() {
    const { data } = await CourierApi.getCourierList('inactive');
    const CourierList = data.data;
    setCouriersInactive(CourierList);
  }

  async function getCourierList() {
    try {
      setLoadingPage(true);
      await getCourierListActive();
      await getCourierListInactive();
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingPage(false);
    }
  }

  useEffect(() => {
    getCourierList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  async function hideCourier(id) {
    try {
      if (id) {
        await CourierApi.hideCourier(id);
        message.success('successfully hide courier');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getCourierList();
    }
  }

  async function deleteCourier(id) {
    try {
      if (id) {
        await CourierApi.deleteCourier(id);
        message.success('Successfully removed courier');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getCourierList();
    }
  }

  function handleHide(id) {
    confirm({
      title: 'Are you sure you want to hide this courier?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        hideCourier(id);
      },
      onCancel() {},
    });
  }

  function handleDelete(id) {
    confirm({
      title: 'Are you sure you want to hide this courier?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteCourier(id);
      },
      onCancel() {},
    });
  }

  async function handleRestore(id) {
    try {
      if (id) {
        await CourierApi.restoreCourier(id);
        message.success('Successfully restored courier');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getCourierList();
    }
  }

  if (loadingPage) {
    return <PageSpinner />;
  }

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  function SortableItem({ value: courier, index }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: courier.id || index,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      marginTop: 20,
      userSelect: 'none',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="pd-cms-courier--list draggable-item">
        <Row type="flex" align="middle" gutter={30}>
          <Col span={1}>
            <div {...listeners} style={{ cursor: 'grab', padding: '0 10px' }}>
              ⋮⋮
            </div>
          </Col>
          <Col span={4} {...listeners}>
            <div className="pd-cms-courier--picture">
              <img
                className="image"
                src={courier.logo_url}
                alt="logo courier"
              />
            </div>
          </Col>
          <Col span={11} {...listeners}>
            <div className="table-title--sort">{courier.title}</div>
          </Col>
          <Col span={2} {...listeners}>
            <div className="status-indicator">
              <span
                className={courier.status !== 'inactive' ? 'active' : 'hidden'}>
                {displayStatus(courier.status)}
              </span>
            </div>
          </Col>
          <Col span={6}>
            <div className="button-table">
              <Row>
                <Col span={12} className="button">
                  {courier.status === 'active' ? (
                    <PdButton
                      type="danger"
                      outline
                      full
                      style={{ height: 32 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHide(courier.id);
                      }}>
                      Deactivate
                    </PdButton>
                  ) : (
                    <PdButton
                      type="primary"
                      outline
                      full
                      style={{ height: 32 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(courier.id);
                      }}>
                      Reactivate
                    </PdButton>
                  )}
                </Col>
                <Col span={12} className="button">
                  {courier.status === 'active' ? (
                    <Link
                      to={{
                        pathname: "/couriers/edit", search: `id=${courier.id}`,
                      }}>
                      <PdButton type="primary" full style={{ height: 32 }}>
                        Edit
                      </PdButton>
                    </Link>
                  ) : (
                    <PdButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(courier.id);
                      }}
                      type="danger"
                      full
                      style={{ height: 32 }}>
                      Delete
                    </PdButton>
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  function SortableList({ items, onSortEnd }) {
    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = items.findIndex((item, index) => (item.id || index) === active.id);
        const newIndex = items.findIndex((item, index) => (item.id || index) === over.id);
        onSortEnd({ oldIndex, newIndex });
      }
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((item, index) => ({ id: item.id || index }))} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem key={item.id} index={index} value={item} />
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  async function onSortEndActive({ oldIndex, newIndex }) {
    let list = [...couriersActive];
    let temp = list[oldIndex];
    list.splice(oldIndex, 1);
    list.splice(newIndex, 0, temp);

    let payload = [];
    list.forEach((item, index) => {
      let order_attr = {
        id: item.id,
        order_level: index,
      };
      payload.push(order_attr);
    });

    try {
      setCouriersActive(list);
      await CourierApi.updateOrderCourier(payload);
      message.success('Successfully updated couriers');
    } catch {
      message.error('Cannot connect to server, please check connection');
    }
  }

  return (
    <Layout className="pd-cms-courier main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Couriers</div>
          <Content className="pageContent">
            <Row gutter={20}>
              <Col span={24}>
                <div id="drag-content-active">
                  <SortableList
                    items={couriersActive}
                    onSortEnd={onSortEndActive}
                  />
                </div>
              </Col>

              {couriersInactive.length > 0 && (
                <Col span={24} className="hidden-title-container">
                  <div className="pageTitle hiddenTitle">Inactive Couriers</div>
                </Col>
              )}

              <Col span={24}>
                <div id="drag-content-inactive">
                  <SortableList
                    items={couriersInactive}
                  />
                </div>
              </Col>
            </Row>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default { CourierList };
